import { ICPSettingsOverride } from "../../helper/tradeSettingsHelper/tradeSettingsInterfaces";
import { ITradeSettingsStore } from "@/store/tradeSettingsStore";
import { ReactNode } from "react";

// Shared column widths so Settings and CP Overrides align.
// Keyed by font size so headings (e.g. "ADVISE COVER") don't clip at larger
// text settings. Step is +5px per tier as a baseline — add extra to specific
// columns if a heading still wraps.
export type FontSizeSetting = "small" | "medium" | "large" | "x-large";
export const COLUMN_WIDTHS_BY_FONT_SIZE: Record<
  FontSizeSetting,
  {
    bank: string;
    cpMode: string;
    adviseCompetition: string;
    adviseCover: string;
    autoAffirm: string;
    side: string;
    setLevel: string;
    iceberg: string;
    remove: string;
  }
> = {
  small: {
    bank: "w-[105px]",
    cpMode: "w-[105px]",
    adviseCompetition: "w-[105px]",
    adviseCover: "w-[125px]",
    // Auto Affirm header carries an InfoTooltip icon — needs extra room so
    // the icon doesn't clip at the column edge.
    autoAffirm: "w-[95px]",
    side: "w-[105px]",
    setLevel: "w-[120px]",
    iceberg: "w-[75px]",
    remove: "w-[55px]",
  },
  medium: {
    bank: "w-[110px]",
    cpMode: "w-[110px]",
    adviseCompetition: "w-[110px]",
    adviseCover: "w-[130px]",
    autoAffirm: "w-[110px]",
    side: "w-[110px]",
    setLevel: "w-[125px]", // shared by SIDE + SET LEVEL so they stay symmetrical
    iceberg: "w-[80px]",
    remove: "w-[55px]",
  },
  large: {
    bank: "w-[115px]",
    cpMode: "w-[115px]",
    adviseCompetition: "w-[115px]",
    adviseCover: "w-[135px]",
    autoAffirm: "w-[135px]",
    side: "w-[115px]",
    setLevel: "w-[130px]",
    iceberg: "w-[85px]",
    remove: "w-[55px]",
  },
  "x-large": {
    bank: "w-[120px]",
    cpMode: "w-[120px]",
    adviseCompetition: "w-[120px]",
    adviseCover: "w-[140px]",
    autoAffirm: "w-[140px]",
    side: "w-[120px]",
    setLevel: "w-[135px]",
    iceberg: "w-[90px]",
    remove: "w-[55px]",
  },
};

export const GAP = "gap-4";

// --- Settings configuration ---
// To remove a setting from the UI, comment out its object in the array.
// To reorder, move the object to the desired position.
export interface ICPGridSettingConfig {
  settingField: keyof ICPSettingsOverride & keyof ITradeSettingsStore;
  heading: string;
  id: string; // Used for data-testid and id attributes
  columnWidth: string;
  type: "dropdown" | "numeric" | "duration";
  cpOverridable: boolean; // Whether this setting appears in CP Overrides per-bank rows
  hidden?: boolean; // Completely hidden from the global settings row
  disabled?: boolean; // Disabled but visible in the global settings row
  // Trade-only settings (cpOverridable: false) are normally hidden in Edit
  // Panel because the trade-level row resets to defaults on hydration.
  // showInEditPanel — hydrated from GET response, renders editable in Edit Panel.
  // showInEditPanelAsReadOnly — hydrated from GET response, renders disabled in Edit Panel.
  showInEditPanel?: boolean;
  showInEditPanelAsReadOnly?: boolean;
  // ReactNode so a heading tooltip can list options over several lines.
  infoMessage?: ReactNode;
  // Widen that tooltip when the default 220px wraps the content awkwardly.
  infoMessageWidthClass?: string;
  // Numeric-specific
  min?: number;
  max?: number;
  suffix?: string;
  // For per-CP rows: controls whether the control renders for a given bank.
  // The column width reservation still renders to keep alignment.
  isCPCellVisible?: (cpCode: string) => boolean;
}
